import React, { useCallback, useEffect, useRef, useState } from 'react';
import { FileDetailModal, MoFile } from '../components/FileDetailModal';
import { eventDateFromFilename } from '../utils/filenameDate';
// 행사 시간 ↔ 종료 시각 양방향 연동 — 업로드 문진·영상 상세와 **같은 규칙**(단일 정의처).
import { durationFromEnd, endFromDuration, humanizeMinutes, isRangeInvalid, localInputPlusMinutes, onStartChanged, snapOnStartBlur } from '../utils/eventDuration';
// 변환 진행률 — App.tsx 가 /ws 의 transcode_progress/complete/failed 를 이 스토어로 넣는다.
// BASIC 은 폴링하지 않고(규약) 이 푸시만 구독해 "영상 준비 중" 배지를 진행률로 바꾸고,
// 완료 푸시가 오면 overview 를 다시 읽는다. 푸시를 놓친 경우를 위해 새로 고침 버튼을 둔다.
import { useFileTranscodeStore } from '../stores/fileTranscodeStore';
import { transcodeBadge, variantsBadge } from '../utils/transcodeBadge';
import { uploadWithProgress } from '../utils/uploadWithProgress';
import { useAppStore } from '../App';

/**
 * BASIC 모드 (Phase 1) — "업로드 = 재생".
 *
 * 원칙(2026-08-27 합의):
 *  - 사용자는 모패밀리·슬롯·박스·바터를 몰라야 한다. 표면 어휘는 "내 화면",
 *    "제휴 장소 화면", "홍보 기간" 뿐.
 *  - 격리: 본체 컴포넌트·서비스 수정 0. 서버는 기존 경로(/mofile/upload,
 *    /mofile/:hash/recipe=applyRecipe)를 그대로 호출하고, 읽기는 /basic-api/overview 만.
 *    이 파일 + src/routes/basic.ts + 마운트 줄들을 지우면 BASIC 은 흔적 없이 사라진다.
 *  - BASIC 이 만든 데이터는 전부 보통 데이터 — Advanced 화면에서 그대로 보이고 조작된다.
 *
 * Phase 2(예정): 제휴(바터) 요청/수락 원버튼 + Public 보장 + "다른 장소 소식 -" 라벨.
 */

interface ScreenEntry {
 slotNumber: number;
 fileHash: string;
 fromPartner: boolean;
 filename: string;
 thumbnailPath: string | null;
 transcodeStatus: string;
 transcodeProgress?: number | null;   // 내 파일만(파트너 파일은 null)
 preparing?: boolean | null;          // 사진 준비 단계 마커
 transcodeError?: string | null;      // 사용자 문장
 eventStartAt: string | null;
 expiresAt: string | null;
 publishAt: string | null;
 filtered: boolean;
 sourceKey?: string | null; // 제휴 유입 카드의 출처 장소(providerFamilyHash) — 하이라이트용.
}

interface Overview {
 family: { moFamilyHash: string; name: string } | null;
 screen: ScreenEntry[];
 publicReady: boolean;
 partners: { key: string; partnerName: string; wired: boolean }[];
 outbox: {
 itemId: number; fileHash: string; filename: string;
 thumbnailPath: string | null; transcodeStatus: string;
 transcodeProgress?: number | null; preparing?: boolean | null; transcodeError?: string | null;
 startAt: string | null; effectiveEnd: string | null;
 }[];
 publicBoxId: number | null;
 setupNeeded: boolean;
 requests: {
 incoming: { id: number; name: string }[];
 outgoing: { id: number; name: string }[];
 };
}

// overview 의 DB 상태를 배지 스토어에 반영(newest-wins — 방금 WS 로 갱신된 항목은 덮지 않는다).
// 새로고침 후에도 %·단계·실패 사유가 보이고, WS 를 못 받는 가족 구성원 탭에서도 seed 가 근거가 된다.
const seedOverview = (data: Overview | null) => {
 if (!data) return;
 const seed = useFileTranscodeStore.getState().seed;
 const rows = [...data.screen.filter(it => !it.fromPartner), ...(data.outbox ?? [])];
 for (const it of rows) {
 const st = it.transcodeStatus;
 if (st !== 'pending' && st !== 'processing' && st !== 'failed' && st !== 'done') continue;
 seed(it.fileHash, null, {
 status: st, progress: it.transcodeProgress ?? 0,
 preparing: it.preparing ?? false, error: it.transcodeError ?? undefined,
 });
 }
};

const fmtDate = (iso: string | null) => {
 if (!iso) return null;
 const d = new Date(iso);
 return `${d.getMonth() + 1}/${d.getDate()} ${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
};

const BasicPage: React.FC = () => {
 const [overview, setOverview] = useState<Overview | null>(null);
 const [loading, setLoading] = useState(true);
 // 업로드 진행
 const [uploading, setUploading] = useState(false);
 const [progress, setProgress] = useState(0);
 // 전송 완료 후 서버 처리(이미지→영상 변환·썸네일) 대기 구간 — 진행률이 100%에서 멈춰
 // 오류처럼 보이던 문제. 별도 단계로 표시(스피너+안내), 응답이 오면 해제.
 const [processing, setProcessing] = useState<null | 'image' | 'video'>(null);
 const fileInputRef = useRef<HTMLInputElement>(null);
 // 업로드 완료 → 일정 문진
 const [pending, setPending] = useState<{ fileHash: string; title: string } | null>(null);
 const [eventStartAt, setEventStartAt] = useState('');
 const [promoStartAt, setPromoStartAt] = useState('');
 const [promoEndAt, setPromoEndAt] = useState('');
 const [keepForever, setKeepForever] = useState(false);
 // 행사 시간(분) — 저장 필드가 아니라 행사 일시↔"언제까지"(홍보 종료)를 물리는 입력 보조.
 const [durationMin, setDurationMin] = useState('');
 // "언제부터"를 직접 만졌는가 — 만졌으면 자동 프리필(종료−7일)이 덮지 않는다.
 const [promoTouched, setPromoTouched] = useState(false);
 // 홍보 시작 프리필 규약(2026-09-09 확정) = 홍보 종료(행사시각+행사시간)의 7일 전
 // — 서버 promoWindow 기본(종료−1주)과 같은 규칙. 과거가 되면 서버가 '즉시'로 처리.
 const promoFromEnd = (endLocal: string) => (endLocal ? localInputPlusMinutes(endLocal, -7 * 24 * 60) : '');
 const [autoPurge, setAutoPurge] = useState(true);
 const [toPublic, setToPublic] = useState(false);
 const [submitting, setSubmitting] = useState(false);
 const [notice, setNotice] = useState<string[] | null>(null);
 // 제휴 요청 모달
 const [showRequest, setShowRequest] = useState(false);
 const [searchQ, setSearchQ] = useState('');
 const [searchResults, setSearchResults] = useState<{ moPeerHash: string; name: string | null; email: string }[]>([]);
 const [requesting, setRequesting] = useState(false);
 const [responding, setResponding] = useState<number | null>(null);
 const [cleaning, setCleaning] = useState(false);
 // 제휴 장소 카드 클릭 시, 그 장소에서 온 '다른 장소 소식' 카드만 하이라이트(토글).
 const [highlightKey, setHighlightKey] = useState<string | null>(null);

 // 제휴 셋업 reconcile — setupNeeded 일 때 1회만(중복 방지 ref). 멱등이라 재호출도 무해.
 const setupRan = useRef(false);
 const loadOverview = useCallback(async () => {
 try {
 const r = await fetch('/basic-api/overview', { credentials: 'include' });
 const j = await r.json();
 if (j.success) {
 setOverview(j.data);
 seedOverview(j.data);
 if (j.data.setupNeeded && !setupRan.current) {
 setupRan.current = true;
 const s = await fetch('/basic-api/partner/setup', { method: 'POST', credentials: 'include' });
 const sj = await s.json().catch(() => ({}));
 if (sj.success) {
 const msgs = [...(sj.data?.actions ?? []), ...(sj.data?.warnings ?? []).map((w: string) => `⚠️ ${w}`)];
 if (msgs.length > 0) setNotice(msgs);
 const r2 = await fetch('/basic-api/overview', { credentials: 'include' });
 const j2 = await r2.json();
 if (j2.success) { setOverview(j2.data); seedOverview(j2.data); }
 }
 }
 }
 } catch { /* 다음 새로고침에 수습 */ }
 setLoading(false);
 }, []);

 useEffect(() => {
 void loadOverview();
 // 인코딩 완료 등은 창 복귀 시 반영(주기 폴링 금지 — 프로젝트 규약).
 const onFocus = () => void loadOverview();
 window.addEventListener('focus', onFocus);
 return () => window.removeEventListener('focus', onFocus);
 }, [loadOverview]);

 // 업로드 준비 폼 — 파일 선택 직후 제목·동적 라벨(+이미지면 노출 시간·너비)을 받는다.
 // 행사·홍보 일정은 업로드 뒤 문진이 담당(중복 질문 금지). 이미지는 서버가 영상으로 변환.
 const [prep, setPrep] = useState<{
 file: File; isImage: boolean; title: string; eventLabel: string;
 durationSecs: string; width: string;
 } | null>(null);

 const pickFile = (file: File) => {
 const isImage = file.type.startsWith('image/');
 if (!isImage && !file.type.startsWith('video/')) {
 alert('영상 또는 이미지 파일만 올릴 수 있어요.');
 return;
 }
 // 너비 기본 2160(4K 세로 설치 대비, 사용자 확정 2026-08-27) — 원본 폭 무관 고정 기본.
 setPrep({ file, isImage, title: '', eventLabel: '', durationSecs: '10', width: '2160' });
 };

 const startUpload = (p: NonNullable<typeof prep>) => {
 const formData = new FormData();
 formData.append('file', p.file);
 if (p.title.trim()) formData.append('title', p.title.trim());
 if (p.eventLabel.trim()) formData.append('event_label', p.eventLabel.trim());
 if (p.isImage) {
 const dur = parseFloat(p.durationSecs);
 const w = parseInt(p.width, 10);
 if (!Number.isFinite(dur) || dur <= 0 || !Number.isInteger(w) || w <= 0) {
 alert('사진은 보여줄 시간(초)과 너비(px)를 적어 주세요.');
 return;
 }
 formData.append('image_duration_secs', String(dur));
 formData.append('image_width', String(w));
 }
 setPrep(null);
 setUploading(true);
 setProgress(0);
 // 바이트 진행률 + "바이트 다 나감"(Chromium onload 지연 우회)은 공용 헬퍼가 처리한다.
 const { promise } = uploadWithProgress({
 url: '/mofile/upload', formData,
 onProgress: (pct) => setProgress(pct),
 onBytesDone: () => setProcessing(p.isImage ? 'image' : 'video'),
 });
 promise.then(({ status, json }) => {
 setUploading(false);
 setProcessing(null);
 if (status === 200 && json?.success) {
 // 업로드 직후 일정 문진으로 — "업로드 = 재생" 약속의 절반.
 setPending({ fileHash: json.data.file_hash, title: json.data.filename || p.file.name });
 // 파일명 날짜("20260909"·"20260827-1900") → 행사 일시 프리필 + 종료 연동.
 const fnDate = eventDateFromFilename(p.file.name);
 if (fnDate) {
 const r2 = onStartChanged(fnDate, '');
 setEventStartAt(r2.start);
 setPromoEndAt(r2.end);
 setDurationMin(r2.durationMin);
 setPromoStartAt(promoFromEnd(r2.end)); // 규약: 종료−7일
 } else {
 setEventStartAt('');
 setPromoEndAt('');
 setDurationMin('');
 setPromoStartAt('');
 }
 setPromoTouched(false);
 setKeepForever(false);
 setAutoPurge(true);
 setToPublic(false);
 setNotice(null);
 } else {
 alert(json?.error || '올리지 못했어요.');
 }
 }).catch(() => { setUploading(false); setProcessing(null); alert('올리는 중 문제가 생겼어요.'); });
 };

 // 문진 제출 → 기존 편성 기계(applyRecipe). placement=slot: 빈 자리에 바로,
 // 자리가 없으면 서버가 자동으로 늘린다(상한 도달 등은 경고로 돌아옴).
 const submitSchedule = async () => {
 if (!pending) return;
 if (!keepForever && !promoEndAt) {
 alert('언제까지 보여줄지 정하거나, "계속 보여주기"를 선택해 주세요.');
 return;
 }
 if (!keepForever && isRangeInvalid(eventStartAt, promoEndAt)) {
 alert('종료가 행사 일시보다 이릅니다 — 종료 시각을 확인해 주세요.');
 return;
 }
 setSubmitting(true);
 try {
 const toIso = (v: string) => (v ? new Date(v).toISOString() : null);
 const r = await fetch(`/mofile/${pending.fileHash}/recipe`, {
 method: 'POST',
 credentials: 'include',
 headers: { 'Content-Type': 'application/json' },
 body: JSON.stringify({
 event_start_at: toIso(eventStartAt),
 event_end_at: null,
 promo_start_at: toIso(promoStartAt), // 비우면 지금부터 (미래면 publish_at 예약)
 promo_end_at: keepForever ? null : toIso(promoEndAt),
 placement: 'slot',
 is_public: toPublic,
 }),
 });
 const j = await r.json().catch(() => ({}));
 if (!r.ok || !j.success) {
 alert(j.error || '등록에 실패했어요.');
 } else {
 // 만료 후 파일 자동 정리 opt-in — 홍보 종료가 있고 체크됐을 때만.
 const purge = !keepForever && !!promoEndAt && autoPurge;
 try {
 await fetch(`/mofile/${pending.fileHash}/purge-after-expiry`, {
 method: 'PUT', credentials: 'include',
 headers: { 'Content-Type': 'application/json' },
 body: JSON.stringify({ enabled: purge }),
 });
 } catch { /* 플래그 실패는 편성을 막지 않는다 */ }
 // 영수증 → BASIC 어휘로 번역
 const msgs: string[] = [];
 const placements: any[] = j.data?.placements ?? [];
 const pub = placements.find(p => p.kind === 'publish_start' && p.at);
 if (placements.some(p => p.kind === 'direct_slot')) {
 msgs.push(pub
 ? `내 화면에 예약했어요 — ${fmtDate(pub.at)} 부터 나가기 시작해요. 그때까지는 화면에 나오지 않아요.`
 : (transcodeOf(pending.fileHash)?.status === 'failed'
 ? '내 화면에 등록했지만 게재 준비에 실패했어요. 카드를 눌러 확인해 주세요.'
 : '내 화면에 등록했어요. 게재 준비가 끝나는 대로 재생돼요.'));
 }
 if (placements.some(p => p.kind === 'public_box')) msgs.push('제휴 장소 화면에도 내보냈어요.');
 const exp = placements.find(p => p.kind === 'expiry' && p.at);
 if (exp) msgs.push(`${fmtDate(exp.at)} 까지 보여드리고 자동으로 내려가요.`);
 else if (keepForever) msgs.push('내리기 전까지 계속 보여드려요.');
 for (const w of (j.data?.warnings ?? [])) msgs.push(`⚠️ ${w}`);
 setNotice(msgs);
 setPending(null);
 void loadOverview();
 }
 } catch {
 alert('등록에 실패했어요.');
 }
 setSubmitting(false);
 };

 // 제휴 상대 검색 — 기존 /mopeer/search 재사용(2글자 이상).
 const searchPeers = async (q: string) => {
 setSearchQ(q);
 if (q.trim().length < 2) { setSearchResults([]); return; }
 try {
 const r = await fetch(`/mopeer/search?q=${encodeURIComponent(q.trim())}`, { credentials: 'include' });
 const j = await r.json();
 if (j.success) setSearchResults(j.data ?? []);
 } catch { /* 검색 실패는 무시 */ }
 };

 const sendRequest = async (toPeerHash: string) => {
 setRequesting(true);
 try {
 const r = await fetch('/basic-api/partner/request', {
 method: 'POST', credentials: 'include',
 headers: { 'Content-Type': 'application/json' },
 body: JSON.stringify({ toPeerHash }),
 });
 const j = await r.json().catch(() => ({}));
 if (j.success) {
 setNotice(['제휴 요청을 보냈어요. 상대가 수락하면 서로의 화면에 소식을 내보내요.']);
 setShowRequest(false);
 void loadOverview();
 } else {
 alert(j.error || '요청에 실패했어요.');
 if (j.code === 'REVERSE_PENDING') { setShowRequest(false); void loadOverview(); }
 }
 } catch { alert('요청에 실패했어요.'); }
 setRequesting(false);
 };

 const respondRequest = async (requestId: number, accept: boolean) => {
 setResponding(requestId);
 try {
 const r = await fetch('/basic-api/partner/respond', {
 method: 'POST', credentials: 'include',
 headers: { 'Content-Type': 'application/json' },
 body: JSON.stringify({ requestId, accept }),
 });
 const j = await r.json().catch(() => ({}));
 if (j.success) {
 setNotice(accept
 ? ['제휴가 성사됐어요! 서로의 화면에 한 자리씩 소식 공간이 생겼어요.']
 : ['요청을 거절했어요.']);
 setupRan.current = false; // 새 제휴 — 셋업 reconcile 다시 허용
 void loadOverview();
 } else {
 alert(j.error || '처리에 실패했어요.');
 }
 } catch { alert('처리에 실패했어요.'); }
 setResponding(null);
 };

 // 내 화면 카드 → 제휴 섹션 드롭 = Public 박스에 항목 추가(업로드 문진의 isPublic 과 동일 효과).
 // 항목 창은 파일의 홍보 창을 따른다: 시작 = max(지금, publish_at), 종료 = expires_at(없으면 무기한).
 const [dropHover, setDropHover] = useState(false);
 // 영상 상세 모달 — 영상 목록/스케줄 탭과 같은 모달(/mofile/:hash/info 로 채움).
 // 상대 영상(제휴 유입)은 서버 can_edit 판단으로 읽기 전용.
 const [selectedFile, setSelectedFile] = useState<MoFile | null>(null);
 const openFileDetail = async (fileHash: string, fallbackTitle: string, fallbackThumb: string | null) => {
 try {
 const r = await fetch(`/mofile/${fileHash}/info`, { credentials: 'include' });
 const body = r.ok ? await r.json() : null;
 if (!body?.success || !body.data) { alert('이 영상의 정보를 불러오지 못했어요.'); return; }
 const d = body.data;
 setSelectedFile({
 file_hash: d.file_hash,
 title: d.title ?? fallbackTitle,
 file_type: d.file_type ?? undefined,
 duration: d.duration ?? null,
 thumbnail_path: d.thumbnail_path ?? fallbackThumb ?? undefined,
 event_start_at: d.event_start_at ?? null,
 expires_at: d.expires_at ?? null,
 publish_at: d.publish_at ?? null,
 event_label: d.event_label ?? null,
 overlay_position: d.overlay_position ?? null,
 created_at: '',
 file_url: `/mofile/download/${d.file_hash}`,
 can_edit: d.can_edit === true,
 });
 } catch { alert('이 영상의 정보를 불러오지 못했어요.'); }
 };
 const sendToPartners = async (payload: { fileHash: string; filename: string; publishAt: string | null; expiresAt: string | null }) => {
 const boxId = overview?.publicBoxId;
 if (!boxId) { alert('제휴 송출함이 아직 준비되지 않았어요.'); return; }
 if (overview?.outbox?.some(o => o.fileHash === payload.fileHash)) {
 setNotice([`'${payload.filename}' 은(는) 이미 제휴 화면으로 나가고 있어요.`]);
 return;
 }
 const now = Date.now();
 const startMs = payload.publishAt ? Math.max(now, new Date(payload.publishAt).getTime()) : now;
 try {
 const r = await fetch(`/videobox/${boxId}/items`, {
 method: 'POST', credentials: 'include',
 headers: { 'Content-Type': 'application/json' },
 body: JSON.stringify({
 fileHash: payload.fileHash,
 startAt: new Date(startMs).toISOString(),
 endAt: payload.expiresAt ?? null,
 }),
 });
 const j = await r.json().catch(() => ({}));
 if (j.success) {
 setNotice([`'${payload.filename}' 을(를) 제휴 장소 화면으로 내보냈어요.`]);
 void loadOverview();
 } else {
 alert(j.error || '내보내기에 실패했어요.');
 }
 } catch { alert('내보내기에 실패했어요.'); }
 };

 const screen = overview?.screen ?? [];

 // ── 게재 준비 표시 ─────────────────────────────────────────────────────────
 // 내 카드(내 소식·내보낸 소식)의 서버 상태 + WS 스토어를 공용 배지 유틸(transcodeBadge)로 문구화.
 const transcodeEntries = useFileTranscodeStore(s => s.entries);
 const wsConnected = useAppStore(s => !!s.ws);
 const myCards = [...screen.filter(it => !it.fromPartner), ...(overview?.outbox ?? [])];
 // 대표 영상(variant 아님)의 푸시 상태. 없으면 undefined — seed 만 있거나 푸시를 못 받은 것.
 const transcodeOf = (fileHash: string) => transcodeEntries.get(`${fileHash}::main`);
 const isFailed = (it: { fileHash: string; transcodeStatus: string }) =>
 it.transcodeStatus === 'failed' || transcodeOf(it.fileHash)?.status === 'failed';
 // 아직 준비가 안 끝난 카드(실패 제외 — 실패는 따로 안내한다).
 const pendingHashes = Array.from(new Set(
 myCards.filter(it => it.transcodeStatus !== 'done' && !isFailed(it)).map(it => it.fileHash)
 ));
 const failedHashes = Array.from(new Set(myCards.filter(isFailed).map(it => it.fileHash)));
 const badgeFor = (it: { fileHash: string; transcodeStatus: string; transcodeProgress?: number | null; preparing?: boolean | null; transcodeError?: string | null }) =>
 transcodeBadge({
 server: { status: it.transcodeStatus, progress: it.transcodeProgress, preparing: it.preparing, error: it.transcodeError },
 entry: transcodeOf(it.fileHash),
 wsConnected,
 });
 // 대표가 끝난 뒤에도 화면 맞춤본(회전·크기별)이 남아 있으면 연한 보조 배지.
 const variantBadgeFor = (fileHash: string): string | null => {
 const sts: Array<'pending' | 'processing' | 'done' | 'failed'> = [];
 for (const [k, e] of transcodeEntries) {
 if (k.startsWith(`${fileHash}::`) && !k.endsWith('::main')) sts.push(e.status);
 }
 return variantsBadge(sts);
 };
 const renderPrepBadge = (it: { fileHash: string; transcodeStatus: string; transcodeProgress?: number | null; preparing?: boolean | null; transcodeError?: string | null }) => {
 const b = badgeFor(it);
 if (b.kind === 'none') {
 const vb = variantBadgeFor(it.fileHash);
 return vb ? <span className="absolute top-1 left-1 bg-gray-500/70 text-white text-[10px] px-1.5 py-0.5">{vb}</span> : null;
 }
 const cls = b.kind === 'failed' ? 'bg-red-600/90' : 'bg-gray-800/80';
 return <span className={`absolute top-1 left-1 ${cls} text-white text-[10px] px-1.5 py-0.5`} title={b.error ?? undefined}>{b.text}</span>;
 };
 // 실패 카드: 내리고 지운 뒤 바로 다시 올리기(기존 삭제 API — 슬롯·박스 참조도 함께 정리된다).
 const discardAndReupload = async (fileHash: string, title: string) => {
 if (!confirm(`'${title}' 을(를) 지우고 다시 올릴까요?`)) return;
 try {
 const r = await fetch(`/mofile/${fileHash}`, { method: 'DELETE', credentials: 'include' });
 const j = await r.json().catch(() => ({}));
 if (!r.ok || !j?.success) { alert(j?.error || '지우지 못했어요.'); return; }
 } catch { alert('지우지 못했어요.'); return; }
 await loadOverview();
 fileInputRef.current?.click();
 };
 // 완료 푸시가 오면 overview 를 다시 읽어 배지를 내린다(파일당 1회 — 루프 방지).
 const refreshedForDone = useRef<Set<string>>(new Set());
 useEffect(() => {
 const done = pendingHashes.filter(h => transcodeOf(h)?.status === 'done' && !refreshedForDone.current.has(h));
 if (done.length === 0) return;
 for (const h of done) refreshedForDone.current.add(h);
 // "끝"은 화면에 뜬 것이 아니라 준비가 끝난 것 — 기기가 받아오는 시간을 같이 알린다.
 const titles = done.map(h => myCards.find(e => e.fileHash === h)?.filename).filter((t): t is string => !!t);
 if (titles.length > 0) setNotice(titles.map(t => `'${t}' 게재 준비가 끝났어요. 화면이 받아오는 데 1~2분 걸릴 수 있어요.`));
 void loadOverview();
 // eslint-disable-next-line react-hooks/exhaustive-deps
 }, [transcodeEntries, pendingHashes.join(',')]);
 // 새로 고침 — overview 재조회 + 스토어의 진행 중 항목을 서버 상태와 화해(놓친 완료 푸시 복구).
 const [refreshingTranscode, setRefreshingTranscode] = useState(false);
 const refreshTranscode = async () => {
 setRefreshingTranscode(true);
 try {
 await Promise.all([loadOverview(), useFileTranscodeStore.getState().reconcileStale()]);
 } finally { setRefreshingTranscode(false); }
 };

 // 내 화면 목록을 소식 출처로 나눈다: 내 소식(내가 올림) / 다른 장소 소식(제휴 유입).
 const myScreenMine = screen.filter(it => !it.fromPartner);
 const myScreenPartner = screen.filter(it => it.fromPartner);
 // 홍보 끝난 내 소식 정리 — 내 소식 중 종료된 카드가 있으면 일괄 내리기 버튼을 띄운다.
 const endedMineCount = myScreenMine.filter(it => !!it.expiresAt && new Date(it.expiresAt).getTime() <= Date.now()).length;
 const onCleanupExpired = async () => {
  if (!confirm(`홍보가 끝난 내 소식 ${endedMineCount}개를 내 화면에서 내릴까요?\n(파일은 보관되어 다시 게시할 수 있어요)`)) return;
  setCleaning(true);
  try {
   const r = await fetch('/basic-api/cleanup-expired', { method: 'POST', credentials: 'include' });
   const j = await r.json();
   if (!j.success) { alert(j.error || '정리에 실패했어요.'); return; }
   if (j.data?.message) alert(j.data.message);
   void loadOverview();
  } catch { alert('정리에 실패했어요.'); } finally { setCleaning(false); }
 };

 // 유연한 카드 그리드 — 컬럼 수 제한 없이 뷰포트 폭에 따라 auto-fill.
 // 최소폭 200px = 종전 3컬럼일 때 카드 크기(좁으면 자동으로 줄고 넓으면 늘어난다).
 const cardGrid: React.CSSProperties = {
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))',
  gap: '0.75rem',
 };

 // 내 화면 카드 한 장 — 소식 출처(내/제휴)에 따라 뱃지·버튼이 갈린다. 두 섹션 공용.
 const renderScreenCard = (it: ScreenEntry) => {
  const ended = !!it.expiresAt && new Date(it.expiresAt).getTime() <= Date.now();
  const scheduled = !ended && !!it.publishAt && new Date(it.publishAt).getTime() > Date.now();
  // 하이라이트: 제휴 장소를 선택하면, 그 장소 출처 카드는 파란 링, 그 외 제휴 카드는 흐리게.
  const highlighted = !!highlightKey && it.fromPartner && it.sourceKey === highlightKey;
  const dimmedByHighlight = !!highlightKey && it.fromPartner && it.sourceKey !== highlightKey;
  return (
   <div key={`${it.slotNumber}-${it.fileHash}`}
    className={`border transition ${highlighted ? 'ring-2 ring-blue-400 border-blue-400' : scheduled ? 'border-dashed border-gray-400' : 'border-gray-200'} ${!it.fromPartner ? 'cursor-grab' : ''} ${ended || it.filtered || dimmedByHighlight ? 'opacity-50' : ''}`}
    draggable={!it.fromPartner}
    onDragStart={!it.fromPartner ? (e) => {
     e.dataTransfer.effectAllowed = 'copy';
     e.dataTransfer.setData('application/x-basic-outbox', JSON.stringify({
      fileHash: it.fileHash, filename: it.filename,
      publishAt: it.publishAt, expiresAt: it.expiresAt,
     }));
    } : undefined}
   >
    <div className="relative aspect-video bg-gray-100 cursor-pointer"
     onClick={() => void openFileDetail(it.fileHash, it.filename, it.thumbnailPath)}>
     {it.thumbnailPath && (
      <img src={`/mofile/thumbnail/${it.thumbnailPath}`} alt="" className="absolute inset-0 w-full h-full object-cover" />
     )}
     {renderPrepBadge(it)}
     {ended && (
      <span className="absolute top-1 right-1 bg-gray-700/90 text-white text-[10px] px-1.5 py-0.5">끝남</span>
     )}
     {it.filtered && !ended && (
      <span className="absolute bottom-1 right-1 bg-gray-700/90 text-white text-[10px] px-1.5 py-0.5">안 받는 중</span>
     )}
     {it.publishAt && new Date(it.publishAt).getTime() > Date.now() && (
      <span className="absolute bottom-1 left-1 bg-amber-500/90 text-white text-[10px] px-1.5 py-0.5">
       {fmtDate(it.publishAt)} 부터
      </span>
     )}
    </div>
    <div className="p-2">
     <div className="text-xs font-medium truncate">{it.filename}</div>
     {!it.fromPartner && badgeFor(it).kind === 'failed' && (
      <div className="mt-1">
       {badgeFor(it).error && <div className="text-[11px] text-red-700 leading-snug">{badgeFor(it).error}</div>}
       <button type="button" onClick={(e) => { e.stopPropagation(); void discardAndReupload(it.fileHash, it.filename); }}
        className="mt-1 px-2 py-0.5 text-[11px] font-semibold border border-red-300 text-red-700 bg-white hover:bg-red-50">지우고 다시 올리기</button>
      </div>
     )}
     <div className="flex items-center justify-between mt-0.5">
      <span className="text-[11px] text-gray-500">
       {ended
        ? `${fmtDate(it.expiresAt)} 에 끝났어요`
        : it.expiresAt ? `${fmtDate(it.expiresAt)} 까지` : '계속 재생'}
       {it.eventStartAt ? ` · 행사 ${fmtDate(it.eventStartAt)}` : ''}
      </span>
      {it.fromPartner && (
       <button
        className="text-[11px] text-gray-500 hover:text-gray-800 hover:underline shrink-0 ml-1"
        onClick={async () => {
         const famHash = overview!.family!.moFamilyHash;
         if (!it.filtered && !confirm(`"${it.filename}" 을(를) 내 화면에서 안 받을까요?\n(다른 화면에는 영향이 없어요)`)) return;
         try {
          const r = it.filtered
           ? await fetch(`/videobox/filters/${famHash}/${it.fileHash}`, { method: 'DELETE', credentials: 'include' })
           : await fetch(`/videobox/filters/${famHash}`, {
            method: 'POST', credentials: 'include',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ fileHash: it.fileHash }),
           });
          const j = await r.json().catch(() => ({}));
          if (r.ok && j.success !== false) {
           setNotice([it.filtered
            ? `'${it.filename}' 을(를) 다시 받아요.`
            : `'${it.filename}' 을(를) 내 화면에서 안 받아요.`]);
           void loadOverview();
          } else {
           alert(j.error || '처리에 실패했어요.');
          }
         } catch { alert('처리에 실패했어요.'); }
        }}
       >{it.filtered ? '다시 받기' : '안 받기'}</button>
      )}
      {!it.fromPartner && (
       <button
        className="text-[11px] text-red-500 hover:text-red-700 hover:underline shrink-0 ml-1"
        onClick={async () => {
         if (!confirm(`"${it.filename}" 을(를) 내 화면에서 ${ended ? '정리할까요?' : '내릴까요?'}\n(파일은 그대로 남아요)`)) return;
         try {
          const r = await fetch(`/mofamily/${overview!.family!.moFamilyHash}/slot/${it.slotNumber}/assign`, {
           method: 'POST', credentials: 'include',
           headers: { 'Content-Type': 'application/json' },
           body: JSON.stringify({ file_hash: null }),
          });
          const j = await r.json().catch(() => ({}));
          if (r.ok && j.success !== false) {
           setNotice([`'${it.filename}' 을(를) ${ended ? '정리했어요.' : '내 화면에서 내렸어요.'}`]);
           void loadOverview();
          } else {
           alert(j.error || '내리기에 실패했어요.');
          }
         } catch { alert('내리기에 실패했어요.'); }
        }}
       >{ended ? '정리' : '내리기'}</button>
      )}
     </div>
    </div>
   </div>
  );
 };

 return (
 <div className="max-w-6xl mx-auto p-4 space-y-6">
 {/* 업로드 */}
 <div className="border border-gray-200 p-4">
 <div className="flex items-center justify-between gap-3">
 <div>
 <h1 className="text-lg font-bold">영상·이미지 올리기</h1>
 <p className="text-sm text-gray-500">올리면 내 화면에서 바로 재생돼요.</p>
 </div>
 <button
 className="px-4 py-2 bg-blue-600 text-white disabled:opacity-50"
 disabled={uploading}
 onClick={() => fileInputRef.current?.click()}
 >
 {uploading
 ? (processing
 ? <span className="inline-flex items-center gap-2">
 <span className="w-3.5 h-3.5 border-2 border-white/40 border-t-white rounded-full animate-spin" />
 올리기 끝 · 확인 중
 </span>
 : `올리는 중 ${Math.round(progress)}%`)
 : '파일 선택'}
 </button>
 <input
 ref={fileInputRef}
 type="file"
 accept="video/*,image/*"
 className="hidden"
 onChange={(e) => {
 const f = e.target.files?.[0];
 e.target.value = '';
 if (f) pickFile(f);
 }}
 />
 </div>
 {uploading && (
 <div className="mt-3">
 <div className="h-1.5 bg-gray-200 overflow-hidden">
 {processing ? (
 // 서버 처리 대기 — 길이를 모르는 구간이라 흐르는(indeterminate) 바로 표시.
 <div className="h-full w-1/3 bg-blue-500 animate-[basicSlide_1.2s_ease-in-out_infinite]" />
 ) : (
 <div className="h-full bg-blue-500 transition-all" style={{ width: `${progress}%` }} />
 )}
 </div>
 {processing && (
 <p className="mt-1.5 text-xs text-gray-500 flex items-center gap-1.5">
 <span className="w-3 h-3 border-2 border-gray-300 border-t-blue-500 rounded-full animate-spin" />
 올리기 끝났어요 · 확인하고 있어요 (몇 초 걸려요)
 </p>
 )}
 <style>{`@keyframes basicSlide { 0% { margin-left: -33%; } 100% { margin-left: 100%; } }`}</style>
 </div>
 )}
 {notice && (
 <div className="mt-3 border border-green-200 bg-green-50 p-3 text-sm space-y-1">
 {notice.map((m, i) => <div key={i}>{m}</div>)}
 </div>
 )}
 {/* 변환 진행 안내 — 변환이 안 끝난 내 영상이 있는 동안만. 완료 푸시(WS)를 받으면
     자동으로 사라지고, 푸시를 놓쳤을 때를 위해 새로 고침 버튼을 같이 둔다. */}
 {pendingHashes.length > 0 && !uploading && (
 <div className="mt-3 border border-blue-200 bg-blue-50 p-3 text-sm flex items-center gap-3 flex-wrap">
 <span className="w-3.5 h-3.5 shrink-0 border-2 border-blue-200 border-t-blue-600 rounded-full animate-spin" />
 <span className="flex-1 min-w-[12rem]">
 소식 {pendingHashes.length}개를 게재 준비하고 있어요. 끝나면 카드의 표시가 저절로 사라져요.
 한참 그대로면 새로 고침을 눌러 주세요.
 </span>
 <button
 type="button"
 onClick={() => void refreshTranscode()}
 disabled={refreshingTranscode}
 className="shrink-0 px-2.5 py-1 text-xs font-semibold border bg-white text-blue-700 border-blue-300 hover:bg-blue-100 disabled:opacity-50"
 >{refreshingTranscode ? '확인 중…' : '새로 고침'}</button>
 </div>
 )}
 {failedHashes.length > 0 && !uploading && (
 <div className="mt-3 border border-red-200 bg-red-50 p-3 text-sm text-red-800">
 게재 준비에 실패한 소식 {failedHashes.length}개가 있어요. 카드를 눌러 사유를 확인하고, 지우고 다시 올려 주세요.
 </div>
 )}
 </div>

 {/* 내 화면 — 내 소식(가장 밝은 배경). 각 섹션은 회색 농도로 구분한다. */}
 <div className="bg-gray-50 border border-gray-100 rounded p-3">
 <div className="flex items-center justify-between gap-2 mb-2">
 <h2 className="text-base font-bold">내 화면에서 재생 중인 내 소식{(() => { const n = myScreenMine.filter(it => it.transcodeStatus !== 'done').length; return n > 0 ? <span className="ml-1 text-xs font-normal text-gray-500">(준비 중 {n})</span> : null; })()}</h2>
 {endedMineCount > 0 && (
 <button
 type="button"
 onClick={onCleanupExpired}
 disabled={cleaning}
 className="shrink-0 px-2.5 py-1 text-xs font-semibold border bg-white text-gray-700 border-gray-300 hover:bg-gray-100 disabled:opacity-50"
 >{cleaning ? '정리 중…' : `홍보 끝난 소식 정리 (${endedMineCount})`}</button>
 )}
 </div>
 {loading ? (
 <div className="text-sm text-gray-500">불러오는 중...</div>
 ) : myScreenMine.length === 0 ? (
 <div className="text-sm text-gray-500 border border-dashed border-gray-300 p-6 text-center">
 아직 재생 중인 영상이 없어요. 위에서 영상을 올려보세요.
 </div>
 ) : (
 <div style={cardGrid}>
 {myScreenMine.map(renderScreenCard)}
 </div>
 )}
 <p className="mt-2 text-[11px] text-gray-400">영상이 여러 개면 순서대로 번갈아 재생돼요.</p>
 </div>

 {/* 제휴 장소으로 나가는 중 — Public 박스 항목(원 스펙의 'Public 슬롯 박스 편집').
     내 화면 카드를 끌어다 놓으면 여기(=Public 박스)로 들어간다. */}
 {((overview?.outbox?.length ?? 0) > 0 || ((overview?.partners?.length ?? 0) > 0 && overview?.publicReady)) && (
 <div
 className={`bg-gray-100 border border-gray-200 rounded p-3 ${dropHover ? 'outline outline-2 outline-blue-400 outline-offset-4' : ''}`}
 onDragOver={(e) => {
 if (e.dataTransfer.types.includes('application/x-basic-outbox')) {
 e.preventDefault();
 e.dataTransfer.dropEffect = 'copy';
 setDropHover(true);
 }
 }}
 onDragLeave={(e) => {
 const rel = e.relatedTarget as Node | null;
 if (!rel || !e.currentTarget.contains(rel)) setDropHover(false);
 }}
 onDrop={(e) => {
 setDropHover(false);
 const raw = e.dataTransfer.getData('application/x-basic-outbox');
 if (!raw) return;
 e.preventDefault();
 try { void sendToPartners(JSON.parse(raw)); } catch { /* 무시 */ }
 }}
 >
 <h2 className="text-base font-bold mb-2">제휴 장소에서 재생 중인 내 소식</h2>
 {(overview?.outbox?.length ?? 0) === 0 ? (
 <div className="text-sm text-gray-500 border border-dashed border-gray-300 p-4 text-center">
 지금 제휴 화면으로 나가는 영상이 없어요. 위 "내 화면" 카드를 여기로 끌어오거나,
 업로드할 때 "제휴 장소 화면에도 내보내기"를 체크하면 여기에 담겨요.
 </div>
 ) : (
 <div style={cardGrid}>
 {overview!.outbox.map((it) => {
 const ended = !!it.effectiveEnd && new Date(it.effectiveEnd).getTime() <= Date.now();
 // 예약(미래 시작) = 점선 테두리 — 내 화면 카드와 같은 규약.
 const scheduled = !ended && !!it.startAt && new Date(it.startAt).getTime() > Date.now();
 return (
 <div key={it.itemId} className={`border ${scheduled ? 'border-dashed border-gray-400' : 'border-gray-200'} ${ended ? 'opacity-50' : ''}`}>
 <div className="relative aspect-video bg-gray-100 cursor-pointer"
 onClick={() => void openFileDetail(it.fileHash, it.filename, it.thumbnailPath)}>
 {it.thumbnailPath && (
 <img src={`/mofile/thumbnail/${it.thumbnailPath}`} alt="" className="absolute inset-0 w-full h-full object-cover" />
 )}
 {/* 뱃지는 img 뒤(같은 absolute 겹침에서 위) — 앞에 두면 썸네일에 깔린다. */}
 {ended && (
 <span className="absolute top-1 right-1 bg-gray-700/90 text-white text-[10px] px-1.5 py-0.5">끝남</span>
 )}
 {renderPrepBadge(it)}
 {scheduled && (
 <span className="absolute bottom-1 left-1 bg-amber-500/90 text-white text-[10px] px-1.5 py-0.5">
 {fmtDate(it.startAt)} 부터
 </span>
 )}
 </div>
 <div className="p-2">
 <div className="text-xs font-medium truncate">{it.filename}</div>
 <div className="flex items-center justify-between mt-0.5">
 <span className="text-[11px] text-gray-500">
 {ended
 ? `${fmtDate(it.effectiveEnd)} 에 끝났어요`
 : it.effectiveEnd ? `${fmtDate(it.effectiveEnd)} 까지` : '계속 재생'}
 </span>
 <button
 className="text-[11px] text-red-500 hover:text-red-700 hover:underline"
 onClick={async () => {
 if (!confirm(`"${it.filename}" 을(를) 제휴 화면에서 ${ended ? '정리할까요?' : '내릴까요?'}\n(내 화면과 파일은 그대로 남아요)`)) return;
 try {
 const r = await fetch(`/videobox/items/${it.itemId}`, { method: 'DELETE', credentials: 'include' });
 const j = await r.json().catch(() => ({}));
 if (j.success) { setNotice([`'${it.filename}' 을(를) ${ended ? '정리했어요.' : '제휴 화면에서 내렸어요.'}`]); void loadOverview(); }
 else alert(j.error || '내리기에 실패했어요.');
 } catch { alert('내리기에 실패했어요.'); }
 }}
 >{ended ? '정리' : '내리기'}</button>
 </div>
 </div>
 </div>
 );
 })}
 </div>
 )}
 <p className="mt-2 text-[11px] text-gray-400">여기 담긴 영상이 모든 제휴 장소 화면에서 번갈아 재생돼요. 내 화면 카드를 끌어다 놓으면 추가돼요.</p>
 </div>
 )}

 {/* 제휴 장소 */}
 <div>
 <div className="flex items-center justify-between mb-2">
 <h2 className="text-base font-bold">제휴 장소</h2>
 <button className="px-3 py-1.5 text-sm border border-blue-500 text-blue-600 hover:bg-blue-50"
 onClick={() => { setSearchQ(''); setSearchResults([]); setShowRequest(true); }}>
 제휴 요청
 </button>
 </div>

 {/* 받은 요청 — 수락/거절 */}
 {(overview?.requests?.incoming?.length ?? 0) > 0 && (
 <div className="space-y-2 mb-3">
 {overview!.requests.incoming.map((r) => (
 <div key={r.id} className="flex items-center justify-between border border-blue-200 bg-blue-50 p-3 text-sm">
 <span><b>{r.name}</b> 님이 제휴를 요청했어요 — 수락하면 서로의 화면에 소식을 내보내요.</span>
 <span className="flex gap-2 shrink-0 ml-3">
 <button className="px-3 py-1 bg-blue-600 text-white disabled:opacity-50" disabled={responding === r.id}
 onClick={() => void respondRequest(r.id, true)}>{responding === r.id ? '연결 중...' : '수락'}</button>
 <button className="px-3 py-1 border border-gray-300 disabled:opacity-50" disabled={responding === r.id}
 onClick={() => void respondRequest(r.id, false)}>거절</button>
 </span>
 </div>
 ))}
 </div>
 )}
 {/* 보낸 요청 */}
 {(overview?.requests?.outgoing?.length ?? 0) > 0 && (
 <div className="mb-3 text-sm text-gray-500">
 {overview!.requests.outgoing.map((r) => (
 <div key={r.id}>'{r.name}' 님에게 제휴를 요청했어요 — 응답을 기다리는 중이에요.</div>
 ))}
 </div>
 )}

 {(overview?.partners?.length ?? 0) === 0 ? (
 <div className="text-sm text-gray-500 border border-dashed border-gray-300 p-4 text-center">
 아직 제휴한 장소가 없어요. 제휴를 맺으면 서로의 화면에 소식을 내보낼 수 있어요.
 </div>
 ) : (
 <>
 <div className="flex flex-wrap gap-2">
 {overview!.partners.map((p, i) => {
 const active = highlightKey === p.key;
 return (
 <button
 key={i}
 type="button"
 onClick={() => setHighlightKey(active ? null : p.key)}
 title="이 장소에서 온 소식만 아래에서 강조해요"
 className={`inline-flex items-center gap-1.5 px-3 py-1.5 border text-sm transition ${active ? 'border-blue-400 bg-blue-50 text-blue-700 ring-1 ring-blue-300' : 'border-gray-200 hover:bg-gray-50'}`}
 >
 <span className={`w-2 h-2 rounded-full ${p.wired ? 'bg-green-500' : 'bg-gray-300'}`} />
 {p.partnerName}
 <span className="text-[11px] text-gray-400">{p.wired ? '연결됨' : '연결 준비 중'}</span>
 </button>
 );
 })}
 </div>
 {highlightKey && (
 <p className="mt-2 text-[11px] text-blue-600">
 아래 '다른 장소 소식'에서 선택한 장소의 소식을 강조하고 있어요.
 <button type="button" onClick={() => setHighlightKey(null)} className="ml-1 underline hover:text-blue-800">해제</button>
 </p>
 )}
 </>
 )}
 </div>

 {/* 내 화면 — 다른 장소 소식(제휴 유입, 가장 진한 농도). 드래그 송출(2번째 섹션)
     다음에 두어, 내 소식(1번째)→제휴 송출(2번째) 드래그 거리를 짧게 유지. */}
 {myScreenPartner.length > 0 && (
 <div className="bg-gray-200 border border-gray-300 rounded p-3">
 <h2 className="text-base font-bold mb-2">내 화면에서 재생 중인 다른 장소 소식</h2>
 <div style={cardGrid}>
 {myScreenPartner.map(renderScreenCard)}
 </div>
 <p className="mt-2 text-[11px] text-gray-400">제휴 장소가 보낸 소식이에요. 안 받기로 내 화면에서 뺄 수 있어요.</p>
 </div>
 )}

 {/* 업로드 준비 폼 — 제목·동적 라벨(+이미지: 노출 시간·너비). 일정은 다음 단계 문진이 담당. */}
 {prep && (
 <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
 <div className="bg-white p-5 max-w-sm w-full space-y-3">
 <div>
 <h3 className="font-bold truncate">"{prep.file.name}"</h3>
 <p className="text-sm text-gray-500">올리기 전에 확인해 주세요.</p>
 </div>
 <label className="block text-sm">
 <span className="text-gray-700">제목 (비우면 파일 이름)</span>
 <input className="mt-1 w-full border border-gray-300 px-2 py-1.5 text-sm"
 value={prep.title} onChange={(e) => setPrep({ ...prep, title: e.target.value })} />
 </label>
 <label className="block text-sm">
 <span className="text-gray-700">화면에 함께 띄울 짧은 문구 (선택)</span>
 <input className="mt-1 w-full border border-gray-300 px-2 py-1.5 text-sm" maxLength={60}
 placeholder="예: 8월 30일 (토) 저녁 7시"
 value={prep.eventLabel} onChange={(e) => setPrep({ ...prep, eventLabel: e.target.value })} />
 <span className="text-[11px] text-gray-400">{prep.isImage ? '이미지' : '영상'}에 행사 날짜가 없다면 여기에 적어 주세요 — 화면 위에 함께 표시돼요.</span>
 </label>
 {prep.isImage && (
 <div className="flex gap-3">
 <label className="block text-sm flex-1">
 <span className="text-gray-700">한 번에 보여줄 시간</span>
 <div className="mt-1 flex items-center gap-1">
 <input type="number" min={1} step={1} className="w-full border border-gray-300 px-2 py-1.5 text-sm"
 value={prep.durationSecs} onChange={(e) => setPrep({ ...prep, durationSecs: e.target.value })} />
 <span className="text-sm text-gray-500">초</span>
 </div>
 </label>
 <label className="block text-sm flex-1">
 <span className="text-gray-700">너비</span>
 <div className="mt-1 flex items-center gap-1">
 <input type="number" min={1} className="w-full border border-gray-300 px-2 py-1.5 text-sm"
 value={prep.width} onChange={(e) => setPrep({ ...prep, width: e.target.value })} />
 <span className="text-sm text-gray-500">px</span>
 </div>
 </label>
 </div>
 )}
 <div className="flex justify-end gap-2 pt-1">
 <button className="px-3 py-1.5 text-sm border border-gray-300" onClick={() => setPrep(null)}>취소</button>
 <button className="px-4 py-1.5 text-sm bg-blue-600 text-white" onClick={() => startUpload(prep)}>올리기</button>
 </div>
 </div>
 </div>
 )}

 {/* 영상 상세 모달 — 영상 목록과 같은 모달. 삭제/수정 후엔 목록만 다시 읽으면 된다. */}
 <FileDetailModal
 file={selectedFile}
 readOnly={selectedFile?.can_edit === false}
 onClose={() => setSelectedFile(null)}
 onDelete={() => { setSelectedFile(null); void loadOverview(); }}
 onUpdate={() => { void loadOverview(); }}
 />

 {/* 제휴 요청 모달 */}
 {showRequest && (
 <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
 <div className="bg-white p-5 max-w-sm w-full space-y-3">
 <div>
 <h3 className="font-bold">제휴 요청</h3>
 <p className="text-sm text-gray-500">함께할 장소를 찾아보세요. 서로의 화면에 한 자리씩 소식 공간이 생겨요.</p>
 </div>
 <input
 autoFocus
 className="w-full border border-gray-300 px-2 py-1.5 text-sm"
 placeholder="이름 또는 이메일로 검색 (2글자 이상)"
 value={searchQ}
 onChange={(e) => void searchPeers(e.target.value)}
 />
 <div className="max-h-56 overflow-y-auto divide-y divide-gray-100">
 {searchResults.map((u) => (
 <button key={u.moPeerHash} type="button"
 className="w-full text-left px-2 py-2 hover:bg-gray-50 text-sm flex items-center justify-between disabled:opacity-50"
 disabled={requesting}
 onClick={() => void sendRequest(u.moPeerHash)}>
 <span>{u.name || u.email}</span>
 <span className="text-[11px] text-blue-600">요청 보내기</span>
 </button>
 ))}
 {searchQ.trim().length >= 2 && searchResults.length === 0 && (
 <div className="px-2 py-3 text-sm text-gray-400">검색 결과가 없어요.</div>
 )}
 </div>
 <div className="flex justify-end">
 <button className="px-3 py-1.5 text-sm border border-gray-300" onClick={() => setShowRequest(false)}>닫기</button>
 </div>
 </div>
 </div>
 )}

 {/* 일정 문진 모달 */}
 {pending && (
 <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
 <div className="bg-white p-5 max-w-sm w-full space-y-4">
 <div>
 <h3 className="font-bold">"{pending.title}"</h3>
 <p className="text-sm text-gray-500">언제, 어디까지 보여줄까요?</p>
 </div>
 <label className="block text-sm">
 <span className="text-gray-700">행사 일시 (있다면)</span>
 <input type="datetime-local" className="mt-1 w-full border border-gray-300 px-2 py-1.5 text-sm"
 value={eventStartAt}
 onChange={(e) => {
 // 행사 일시 ↔ 행사 시간(분) ↔ "언제까지" 연동 — 다른 문진·상세 모달과 같은 규칙.
 const r = onStartChanged(e.target.value, durationMin);
 setEventStartAt(r.start);
 if (!keepForever) {
 setPromoEndAt(r.end); setDurationMin(r.durationMin);
 if (!promoTouched) setPromoStartAt(promoFromEnd(r.end)); // 규약: 종료−7일
 }
 }}
 onBlur={() => {
 if (keepForever) return;
 const snap = snapOnStartBlur(eventStartAt, promoEndAt);
 if (snap) {
 setPromoEndAt(snap.end); setDurationMin(snap.durationMin);
 if (!promoTouched) setPromoStartAt(promoFromEnd(snap.end));
 }
 }} />
 </label>
 {eventStartAt && !keepForever && (
 <label className="flex items-center gap-1.5 text-sm text-gray-700">
 행사 시간
 <input type="number" min={1} step={5}
 className="w-20 border border-gray-300 px-2 py-1 text-sm"
 value={durationMin}
 onChange={(e) => {
 setDurationMin(e.target.value);
 const end = endFromDuration(eventStartAt, e.target.value);
 if (end !== null) {
 setPromoEndAt(end);
 if (!promoTouched) setPromoStartAt(promoFromEnd(end));
 }
 }} />
 분
 {humanizeMinutes(Number(durationMin)) && (
 <span className="text-[11px] text-gray-400">({humanizeMinutes(Number(durationMin))})</span>
 )}
 </label>
 )}
 <label className="block text-sm">
 <span className="text-gray-700">언제부터 보여줄까요? (비우면 바로)</span>
 <input type="datetime-local" className="mt-1 w-full border border-gray-300 px-2 py-1.5 text-sm"
 value={promoStartAt} onChange={(e) => { setPromoTouched(true); setPromoStartAt(e.target.value); }} />
 <span className="text-[11px] text-gray-400">미리 올려두면 그 시각부터 자동으로 나가기 시작해요.</span>
 </label>
 <div className="text-sm space-y-1.5">
 <span className="text-gray-700">언제까지 보여줄까요?</span>
 <input type="datetime-local" className="w-full border border-gray-300 px-2 py-1.5 text-sm disabled:bg-gray-100"
 value={promoEndAt} disabled={keepForever}
 onChange={(e) => {
 setPromoEndAt(e.target.value);
 if (eventStartAt) setDurationMin(durationFromEnd(eventStartAt, e.target.value));
 if (!promoTouched && eventStartAt) setPromoStartAt(promoFromEnd(e.target.value));
 }} />
 {!keepForever && isRangeInvalid(eventStartAt, promoEndAt) && (
 <p className="text-[11px] text-red-500">종료가 행사 일시보다 이릅니다</p>
 )}
 <label className="flex items-center gap-2 text-gray-600">
 <input type="checkbox" checked={keepForever} onChange={(e) => setKeepForever(e.target.checked)} />
 계속 보여주기 (직접 내릴 때까지)
 </label>
 {!keepForever && <p className="text-[11px] text-gray-400">끝나면 화면에서 자동으로 내려가요.</p>}
 {!keepForever && (
 <label className="flex items-center gap-2 text-gray-600">
 <input type="checkbox" checked={autoPurge} onChange={(e) => setAutoPurge(e.target.checked)} />
 끝나면 영상 파일도 자동 삭제 (7일 뒤)
 </label>
 )}
 </div>
 <label className={`flex items-center gap-2 text-sm ${overview?.publicReady ? 'text-gray-700' : 'text-gray-400'}`}>
 <input type="checkbox" checked={toPublic} disabled={!overview?.publicReady}
 onChange={(e) => setToPublic(e.target.checked)} />
 제휴 장소 화면에도 내보내기
 {!overview?.publicReady && <span className="text-[11px]">(제휴를 맺으면 열려요)</span>}
 </label>
 <div className="flex justify-end gap-2 pt-1">
 <button className="px-3 py-1.5 text-sm border border-gray-300" disabled={submitting}
 onClick={() => setPending(null)}>나중에</button>
 <button className="px-4 py-1.5 text-sm bg-blue-600 text-white disabled:opacity-50" disabled={submitting}
 onClick={() => void submitSchedule()}>
 {submitting ? '등록 중...' : '화면에 올리기'}
 </button>
 </div>
 </div>
 </div>
 )}
 </div>
 );
};

export default BasicPage;
